import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {TurnstileAction} from "../../app/constants/turnstile_actions";
import {prefetchTurnstileToken} from "../../app/turnstile";
import {useIsMainLeadSendDataReady} from "../../application/hooks/use_is_main_lead_send_data_ready";
import {useSendMainLead} from "../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IApplicationData} from "../../application/types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../application/types/lead/IAmplitudeLeadFormEventTracking";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {isLeadFormSubmitAppError} from "../../application/utils/is_lead_form_submit_app_error";
import {useAppSelector} from "../../store/hooks";
import {getAmplitudeDisplayedCtaText} from "../../tracking/amplitude/get_amplitude_displayed_cta_text";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../user/api/patch_home_match_configuration";
import {getUserFirstName} from "../../user/utils/get_user_first_name";
import {getUnlockChoiceOptions} from "../constants/unlock_choice_options";
import {UNLOCK_CHOICE_PRICE_NEGOTIATION_ARMING_DELAY_MS, UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD} from "../constants/unlock_choice_price_negotiation";
import {UNLOCK_CHOICE_TRACKING} from "../constants/unlock_choice_tracking";
import {markUnlockChoiceContentDelivered, setVendorId} from "../state/registration_flow_slice";
import {trackUnlockChoiceButtonClick, trackUnlockChoiceExitButtonClick, trackUnlockChoiceModalView} from "../tracking/track_unlock_choice_events";
import {IUnlockChoiceData} from "../types/IUnlockChoiceData";
import {IUnlockChoiceLeadRequest} from "../types/IUnlockChoiceLeadRequest";
import {UnlockChoiceOptionId} from "../types/IUnlockChoiceOption";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getUnlockChoiceLeadSource} from "../utils/get_unlock_choice_lead_source";
import {useDeferredModalClose} from "./use_deferred_modal_close";
import {useRegistrationWizardTrackingContext} from "./use_registration_wizard_tracking_context";
import {useUnlockChoiceLeadFormStartTracking} from "./use_unlock_choice_lead_form_start_tracking";
import {useUnlockChoicePriceNegotiation} from "./use_unlock_choice_price_negotiation";

export const UNLOCK_CHOICE_CONFIRMATION_DURATION_MS = 5000;

interface UseUnlockChoiceLeadFlowParams {
    entryPoint: UnlockChoiceEntryPoint;
    data: IUnlockChoiceData | null;
    onLeadSent: (isHomeMatchOn: boolean, sentApplication: IApplicationData | null, leadSource: ApplicationSource) => void;
    onContentOnly: () => void;
    onClose: () => void;
}

interface ILeadResult {
    isHomeMatchOn: boolean;
    application: IApplicationData | null;
    leadSource: ApplicationSource;
}

interface ISentLead extends ILeadResult {
    application: IApplicationData;
    subject: string;
}

interface IPriceNegotiationView {
    exit: "content_only" | "close";
}

export const useUnlockChoiceLeadFlow = (params: UseUnlockChoiceLeadFlowParams) => {
    const {entryPoint, data, onLeadSent, onContentOnly, onClose} = params;
    const vendorId = data?.vendorId ?? null;
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const {propertyId, offerId} = flowState;

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const isLeadDataReady = useIsMainLeadSendDataReady({vendorId, offerId: offerId ?? null, propertyId: propertyId ?? null});
    const priceNegotiation = useUnlockChoicePriceNegotiation({data, offerId: offerId ?? null});
    const wizardTrackingContext = useRegistrationWizardTrackingContext();

    const options = getUnlockChoiceOptions(entryPoint, data?.offerType ?? null);
    const [selectedOptionId, setSelectedOptionId] = useState<UnlockChoiceOptionId>(options[0].id);
    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const [isSending, setIsSending] = useState(false);
    const {isOpen, requestClose, handleAfterClose} = useDeferredModalClose(onClose);
    const [sentLead, setSentLead] = useState<ISentLead | null>(null);
    const [priceNegotiationView, setPriceNegotiationView] = useState<IPriceNegotiationView | null>(null);
    const [isPriceNegotiationArmed, setIsPriceNegotiationArmed] = useState(false);
    const hasOpenedPlanRef = useRef(false);
    const hasReportedLeadRef = useRef(false);
    const hasTrackedViewRef = useRef(false);
    const onLeadSentRef = useRef(onLeadSent);
    const trackingContext = {
        entryPoint,
        authPlacement: wizardTrackingContext.authPlacement,
        location: wizardTrackingContext.location,
        itemId: wizardTrackingContext.itemId,
        propertyId: wizardTrackingContext.propertyId
    };
    const {buttonCategory, values: searchStages} = UNLOCK_CHOICE_TRACKING[entryPoint];
    const priceNegotiationLead: IUnlockChoiceLeadRequest = {...UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD, formLocation: buttonCategory};

    const getLeadSource = () => {
        if (priceNegotiationView) {
            return UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source;
        }

        return selectedOption.sendsLead ? getUnlockChoiceLeadSource(selectedOption.id, {entryPoint, flowLeadSource: flowState.leadSource, propertyId}) : null;
    };

    const leadSource = getLeadSource();
    const leadSourceSection = flowState.leadSourceSection ?? ApplicationSourceSection.MODAL;
    const isPriceNegotiation = priceNegotiationView !== null;
    const isLeadConfirm = isPriceNegotiation || selectedOption.sendsLead;
    const isConfirmDisabled = !data || !userProfile || isSending || sentLead !== null || (isLeadConfirm && !isLeadDataReady);

    const getLeadFormTracking = (lead: IUnlockChoiceLeadRequest, searchStage?: string): IAmplitudeLeadFormTrackingContext | null => {
        const ctaLeadFormTracking = getAmplitudeCtaLeadFormTrackingContext({
            source: lead.source,
            sourceSection: leadSourceSection,
            leadData: undefined,
            ctaText: getAmplitudeDisplayedCtaText(lead.ctaText),
            offerId,
            propertyId,
            viewType,
            formLocation: lead.formLocation
        });

        return ctaLeadFormTracking
            ? {
                  ...ctaLeadFormTracking,
                  inquiry_topics: [],
                  auth_placement: trackingContext.authPlacement,
                  ...(searchStage !== undefined && {rp_search_stage: searchStage})
              }
            : null;
    };

    const leadButtonFormTracking = flowState.unlockChoiceLeadPayload?.amplitudeLeadFormEventTracking;

    useUnlockChoiceLeadFormStartTracking({
        trackingContext:
            entryPoint === UnlockChoiceEntryPoint.LEAD && leadButtonFormTracking
                ? {...leadButtonFormTracking, inquiry_topics: [], auth_placement: trackingContext.authPlacement}
                : null,
        source: flowState.leadSource ?? null,
        sourceSection: flowState.leadSourceSection ?? null,
        isReady: data !== null,
        vendorId
    });

    useUnlockChoiceLeadFormStartTracking({
        trackingContext: isPriceNegotiation ? getLeadFormTracking(priceNegotiationLead) : null,
        source: UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source,
        sourceSection: leadSourceSection,
        isReady: true,
        vendorId
    });

    useEffect(() => {
        onLeadSentRef.current = onLeadSent;
    }, [onLeadSent]);

    useEffect(() => {
        if (data && !hasTrackedViewRef.current) {
            hasTrackedViewRef.current = true;
            trackUnlockChoiceModalView(trackingContext);
        }
    }, [data]);

    useEffect(() => {
        if (vendorId !== null && !flowState.vendorId) {
            dispatch(setVendorId({vendorId}));
        }
    }, [flowState.vendorId, vendorId]);

    useEffect(() => {
        if (isOpen) {
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isPriceNegotiation) {
            return;
        }

        const timeoutId = setTimeout(() => setIsPriceNegotiationArmed(true), UNLOCK_CHOICE_PRICE_NEGOTIATION_ARMING_DELAY_MS);

        return () => clearTimeout(timeoutId);
    }, [isPriceNegotiation]);

    const storeMainLeadData = (source: ApplicationSource) => {
        if (vendorId !== null && offerId) {
            dispatch(
                setMainLeadData({
                    vendorId,
                    offerId,
                    propertyId: propertyId ?? null,
                    source,
                    sourceSection: leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    };

    useEffect(() => {
        if (leadSource !== null) {
            storeMainLeadData(leadSource);
        }
    }, [vendorId, offerId, propertyId, leadSource, leadSourceSection, flowState.gtmClickText, flowState.gtmSourceSection]);

    const reportLead = (lead: ILeadResult) => {
        if (hasReportedLeadRef.current) {
            return;
        }

        hasReportedLeadRef.current = true;
        onLeadSentRef.current(lead.isHomeMatchOn, lead.application, lead.leadSource);
    };

    const reportSentLead = () => {
        if (sentLead) {
            reportLead(sentLead);
        }
    };

    useEffect(() => {
        if (!sentLead) {
            return;
        }

        const timeoutId = setTimeout(reportSentLead, UNLOCK_CHOICE_CONFIRMATION_DURATION_MS);

        return () => clearTimeout(timeoutId);
    }, [sentLead]);

    const sendLead = async (lead: IUnlockChoiceLeadRequest, searchStage?: string) => {
        if (!data || !userProfile) {
            return;
        }

        setIsSending(true);

        const amplitudeLeadFormEventTracking = getLeadFormTracking(lead, searchStage);

        let application: IApplicationData | null = null;

        try {
            application =
                (await sendMainLead(
                    {
                        name: userProfile.name ?? "",
                        email: userProfile.email ?? "",
                        phone: getPhoneDataFromString(userProfile.phone ?? ""),
                        marketing_email: userProfile.email_marketing_consent ?? false,
                        marketing_sms: userProfile.phone_marketing_consent ?? false
                    },
                    undefined,
                    {
                        showSuccessNotification: false,
                        ...(amplitudeLeadFormEventTracking
                            ? {
                                  analyticsParams: {
                                      amplitudeLeadFormEventTracking,
                                      amplitudeLeadFormSubmitContext: {eventAction: "autofill" as const}
                                  }
                              }
                            : {})
                    }
                )) ?? null;
        } catch (error) {
            if (isLeadFormSubmitAppError(error) && error.appError.status === 400) {
                setIsSending(false);
                return;
            }
        }

        const isHomeMatchOn = homeMatchConfig?.is_active ?? false;

        if (!application) {
            reportLead({isHomeMatchOn, application: null, leadSource: lead.source});
            return;
        }

        setSentLead({
            isHomeMatchOn,
            application,
            leadSource: lead.source,
            subject: lead.confirmationSubject({propertyNumber: data.propertyNumber, offerType: data.offerType})
        });
    };

    const openPlanOnce = () => {
        const plan = data?.plan;

        if (entryPoint === UnlockChoiceEntryPoint.FLOOR_PLAN && plan && !hasOpenedPlanRef.current) {
            hasOpenedPlanRef.current = true;
            window.open(plan, "_blank", "noopener,noreferrer");
            dispatch(markUnlockChoiceContentDelivered());
        }
    };

    const showPriceNegotiation = (exit: IPriceNegotiationView["exit"]) => {
        if (!priceNegotiation.isEligible) {
            return false;
        }

        storeMainLeadData(UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source);
        priceNegotiation.markShown();
        setPriceNegotiationView({exit});
        trackUnlockChoiceModalView(trackingContext, "price_negotiate");

        return true;
    };

    const continueAfterPriceNegotiation = (exit: IPriceNegotiationView["exit"]) => {
        if (exit === "content_only") {
            onContentOnly();
            return;
        }

        requestClose();
    };

    const handleConfirm = () => {
        if (isConfirmDisabled) {
            return;
        }

        if (priceNegotiationView) {
            if (isPriceNegotiationArmed) {
                trackUnlockChoiceExitButtonClick(trackingContext, "rp_price_negotiate_submit");
                void sendLead(priceNegotiationLead);
            }

            return;
        }

        trackUnlockChoiceButtonClick(trackingContext, "submit", selectedOption.id);
        openPlanOnce();

        if (selectedOption.sendsLead) {
            if (leadSource !== null) {
                void sendLead(
                    {
                        source: leadSource,
                        ctaText: selectedOption.ctaText,
                        formLocation: buttonCategory,
                        confirmationSubject: selectedOption.confirmationSubject
                    },
                    searchStages[selectedOption.id]
                );
            }

            return;
        }

        if (showPriceNegotiation("content_only")) {
            return;
        }

        onContentOnly();
    };

    const handleSelectOption = (optionId: UnlockChoiceOptionId) => {
        if (!isSending) {
            trackUnlockChoiceButtonClick(trackingContext, "select", optionId);
            setSelectedOptionId(optionId);
        }
    };

    const handleClose = () => {
        if (!data) {
            onClose();
            return;
        }

        if (sentLead) {
            reportSentLead();
            return;
        }

        if (isSending) {
            return;
        }

        if (priceNegotiationView) {
            trackUnlockChoiceExitButtonClick(trackingContext, "rp_price_negotiate_close");
            priceNegotiation.markDismissed();
            continueAfterPriceNegotiation(priceNegotiationView.exit);
            return;
        }

        trackUnlockChoiceButtonClick(trackingContext, "close", selectedOption.id);

        if (showPriceNegotiation("close")) {
            return;
        }

        requestClose();
    };

    return {
        isOpen,
        firstName: getUserFirstName(userProfile?.name),
        selectedOptionId: selectedOption.id,
        isSending,
        isConfirmDisabled,
        confirmation: sentLead ? {vendorName: data?.vendorName ?? null, subject: sentLead.subject} : null,
        isPriceNegotiationShown: isPriceNegotiation,
        isPriceNegotiationArmed,
        handleSelectOption,
        handleConfirm,
        handleClose,
        handleAfterClose
    };
};
import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";

import {getPhoneDataFromString} from "@pg-mono/string-utils";

import {TurnstileAction} from "../../app/constants/turnstile_actions";
import {prefetchTurnstileToken} from "../../app/turnstile";
import {useIsMainLeadSendDataReady} from "../../application/hooks/use_is_main_lead_send_data_ready";
import {useSendMainLead} from "../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {IApplicationData} from "../../application/types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../application/types/lead/IAmplitudeLeadFormEventTracking";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {isLeadFormSubmitAppError} from "../../application/utils/is_lead_form_submit_app_error";
import {useAppSelector} from "../../store/hooks";
import {getAmplitudeDisplayedCtaText} from "../../tracking/amplitude/get_amplitude_displayed_cta_text";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../user/api/patch_home_match_configuration";
import {getUserFirstName} from "../../user/utils/get_user_first_name";
import {getUnlockChoiceOptions} from "../constants/unlock_choice_options";
import {UNLOCK_CHOICE_PRICE_NEGOTIATION_ARMING_DELAY_MS, UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD} from "../constants/unlock_choice_price_negotiation";
import {UNLOCK_CHOICE_TRACKING} from "../constants/unlock_choice_tracking";
import {markUnlockChoiceContentDelivered, setVendorId} from "../state/registration_flow_slice";
import {trackUnlockChoiceButtonClick, trackUnlockChoiceExitButtonClick, trackUnlockChoiceModalView} from "../tracking/track_unlock_choice_events";
import {IUnlockChoiceData} from "../types/IUnlockChoiceData";
import {IUnlockChoiceLeadRequest} from "../types/IUnlockChoiceLeadRequest";
import {UnlockChoiceOptionId} from "../types/IUnlockChoiceOption";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getUnlockChoiceLeadSource} from "../utils/get_unlock_choice_lead_source";
import {useDeferredModalClose} from "./use_deferred_modal_close";
import {useRegistrationWizardTrackingContext} from "./use_registration_wizard_tracking_context";
import {useUnlockChoiceLeadFormStartTracking} from "./use_unlock_choice_lead_form_start_tracking";
import {useUnlockChoicePriceNegotiation} from "./use_unlock_choice_price_negotiation";

export const UNLOCK_CHOICE_CONFIRMATION_DURATION_MS = 5000;

interface UseUnlockChoiceLeadFlowParams {
    entryPoint: UnlockChoiceEntryPoint;
    data: IUnlockChoiceData | null;
    onLeadSent: (isHomeMatchOn: boolean, sentApplication: IApplicationData | null, leadSource: ApplicationSource) => void;
    onContentOnly: () => void;
    onClose: () => void;
}

interface ILeadResult {
    isHomeMatchOn: boolean;
    application: IApplicationData | null;
    leadSource: ApplicationSource;
}

interface ISentLead extends ILeadResult {
    application: IApplicationData;
    subject: string;
}

interface IPriceNegotiationView {
    exit: "content_only" | "close";
}

export const useUnlockChoiceLeadFlow = (params: UseUnlockChoiceLeadFlowParams) => {
    const {entryPoint, data, onLeadSent, onContentOnly, onClose} = params;
    const vendorId = data?.vendorId ?? null;
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const {propertyId, offerId} = flowState;

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const isLeadDataReady = useIsMainLeadSendDataReady({vendorId, offerId: offerId ?? null, propertyId: propertyId ?? null});
    const priceNegotiation = useUnlockChoicePriceNegotiation({data, offerId: offerId ?? null});
    const wizardTrackingContext = useRegistrationWizardTrackingContext();

    const options = getUnlockChoiceOptions(entryPoint, data?.offerType ?? null);
    const [selectedOptionId, setSelectedOptionId] = useState<UnlockChoiceOptionId>(options[0].id);
    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const [isSending, setIsSending] = useState(false);
    const {isOpen, requestClose, handleAfterClose} = useDeferredModalClose(onClose);
    const [sentLead, setSentLead] = useState<ISentLead | null>(null);
    const [priceNegotiationView, setPriceNegotiationView] = useState<IPriceNegotiationView | null>(null);
    const [isPriceNegotiationArmed, setIsPriceNegotiationArmed] = useState(false);
    const hasOpenedPlanRef = useRef(false);
    const hasReportedLeadRef = useRef(false);
    const hasTrackedViewRef = useRef(false);
    const onLeadSentRef = useRef(onLeadSent);
    const trackingContext = {
        entryPoint,
        authPlacement: wizardTrackingContext.authPlacement,
        location: wizardTrackingContext.location,
        itemId: wizardTrackingContext.itemId,
        propertyId: wizardTrackingContext.propertyId
    };
    const {buttonCategory, values: searchStages} = UNLOCK_CHOICE_TRACKING[entryPoint];
    const priceNegotiationLead: IUnlockChoiceLeadRequest = {...UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD, formLocation: buttonCategory};

    const getLeadSource = () => {
        if (priceNegotiationView) {
            return UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source;
        }

        return selectedOption.sendsLead ? getUnlockChoiceLeadSource(selectedOption.id, {entryPoint, flowLeadSource: flowState.leadSource, propertyId}) : null;
    };

    const leadSource = getLeadSource();
    const leadSourceSection = flowState.leadSourceSection ?? ApplicationSourceSection.MODAL;
    const isPriceNegotiation = priceNegotiationView !== null;
    const isLeadConfirm = isPriceNegotiation || selectedOption.sendsLead;
    const isConfirmDisabled = !data || !userProfile || isSending || sentLead !== null || (isLeadConfirm && !isLeadDataReady);

    const getLeadFormTracking = (lead: IUnlockChoiceLeadRequest, searchStage?: string): IAmplitudeLeadFormTrackingContext | null => {
        const ctaLeadFormTracking = getAmplitudeCtaLeadFormTrackingContext({
            source: lead.source,
            sourceSection: leadSourceSection,
            leadData: undefined,
            ctaText: getAmplitudeDisplayedCtaText(lead.ctaText),
            offerId,
            propertyId,
            viewType,
            formLocation: lead.formLocation
        });

        return ctaLeadFormTracking
            ? {
                  ...ctaLeadFormTracking,
                  inquiry_topics: [],
                  auth_placement: trackingContext.authPlacement,
                  ...(searchStage !== undefined && {rp_search_stage: searchStage})
              }
            : null;
    };

    const leadButtonFormTracking = flowState.unlockChoiceLeadPayload?.amplitudeLeadFormEventTracking;

    useUnlockChoiceLeadFormStartTracking({
        trackingContext:
            entryPoint === UnlockChoiceEntryPoint.LEAD && leadButtonFormTracking
                ? {...leadButtonFormTracking, inquiry_topics: [], auth_placement: trackingContext.authPlacement}
                : null,
        source: flowState.leadSource ?? null,
        sourceSection: flowState.leadSourceSection ?? null,
        isReady: data !== null,
        vendorId
    });

    useUnlockChoiceLeadFormStartTracking({
        trackingContext: isPriceNegotiation ? getLeadFormTracking(priceNegotiationLead) : null,
        source: UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source,
        sourceSection: leadSourceSection,
        isReady: true,
        vendorId
    });

    useEffect(() => {
        onLeadSentRef.current = onLeadSent;
    }, [onLeadSent]);

    useEffect(() => {
        if (data && !hasTrackedViewRef.current) {
            hasTrackedViewRef.current = true;
            trackUnlockChoiceModalView(trackingContext);
        }
    }, [data]);

    useEffect(() => {
        if (vendorId !== null && !flowState.vendorId) {
            dispatch(setVendorId({vendorId}));
        }
    }, [flowState.vendorId, vendorId]);

    useEffect(() => {
        if (isOpen) {
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isPriceNegotiation) {
            return;
        }

        const timeoutId = setTimeout(() => setIsPriceNegotiationArmed(true), UNLOCK_CHOICE_PRICE_NEGOTIATION_ARMING_DELAY_MS);

        return () => clearTimeout(timeoutId);
    }, [isPriceNegotiation]);

    const storeMainLeadData = (source: ApplicationSource) => {
        if (vendorId !== null && offerId) {
            dispatch(
                setMainLeadData({
                    vendorId,
                    offerId,
                    propertyId: propertyId ?? null,
                    source,
                    sourceSection: leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    };

    useEffect(() => {
        if (leadSource !== null) {
            storeMainLeadData(leadSource);
        }
    }, [vendorId, offerId, propertyId, leadSource, leadSourceSection, flowState.gtmClickText, flowState.gtmSourceSection]);

    const reportLead = (lead: ILeadResult) => {
        if (hasReportedLeadRef.current) {
            return;
        }

        hasReportedLeadRef.current = true;
        onLeadSentRef.current(lead.isHomeMatchOn, lead.application, lead.leadSource);
    };

    const reportSentLead = () => {
        if (sentLead) {
            reportLead(sentLead);
        }
    };

    useEffect(() => {
        if (!sentLead) {
            return;
        }

        const timeoutId = setTimeout(reportSentLead, UNLOCK_CHOICE_CONFIRMATION_DURATION_MS);

        return () => clearTimeout(timeoutId);
    }, [sentLead]);

    const sendLead = async (lead: IUnlockChoiceLeadRequest, searchStage?: string) => {
        if (!data || !userProfile) {
            return;
        }

        setIsSending(true);

        const amplitudeLeadFormEventTracking = getLeadFormTracking(lead, searchStage);

        let application: IApplicationData | null = null;

        try {
            application =
                (await sendMainLead(
                    {
                        name: userProfile.name ?? "",
                        email: userProfile.email ?? "",
                        phone: getPhoneDataFromString(userProfile.phone ?? ""),
                        marketing_email: userProfile.email_marketing_consent ?? false,
                        marketing_sms: userProfile.phone_marketing_consent ?? false
                    },
                    undefined,
                    {
                        showSuccessNotification: false,
                        ...(amplitudeLeadFormEventTracking
                            ? {
                                  analyticsParams: {
                                      amplitudeLeadFormEventTracking,
                                      amplitudeLeadFormSubmitContext: {eventAction: "autofill" as const}
                                  }
                              }
                            : {})
                    }
                )) ?? null;
        } catch (error) {
            if (isLeadFormSubmitAppError(error) && error.appError.status === 400) {
                setIsSending(false);
                return;
            }
        }

        const isHomeMatchOn = homeMatchConfig?.is_active ?? false;

        if (!application) {
            reportLead({isHomeMatchOn, application: null, leadSource: lead.source});
            return;
        }

        setSentLead({
            isHomeMatchOn,
            application,
            leadSource: lead.source,
            subject: lead.confirmationSubject({propertyNumber: data.propertyNumber, offerType: data.offerType})
        });
    };

    const openPlanOnce = () => {
        const plan = data?.plan;

        if (entryPoint === UnlockChoiceEntryPoint.FLOOR_PLAN && plan && !hasOpenedPlanRef.current) {
            hasOpenedPlanRef.current = true;
            window.open(plan, "_blank", "noopener,noreferrer");
            dispatch(markUnlockChoiceContentDelivered());
        }
    };

    const showPriceNegotiation = (exit: IPriceNegotiationView["exit"]) => {
        if (!priceNegotiation.isEligible) {
            return false;
        }

        storeMainLeadData(UNLOCK_CHOICE_PRICE_NEGOTIATION_LEAD.source);
        priceNegotiation.markShown();
        setPriceNegotiationView({exit});
        trackUnlockChoiceModalView(trackingContext, "price_negotiate");

        return true;
    };

    const continueAfterPriceNegotiation = (exit: IPriceNegotiationView["exit"]) => {
        if (exit === "content_only") {
            onContentOnly();
            return;
        }

        requestClose();
    };

    const handleConfirm = () => {
        if (isConfirmDisabled) {
            return;
        }

        if (priceNegotiationView) {
            if (isPriceNegotiationArmed) {
                trackUnlockChoiceExitButtonClick(trackingContext, "rp_price_negotiate_submit");
                void sendLead(priceNegotiationLead);
            }

            return;
        }

        trackUnlockChoiceButtonClick(trackingContext, "submit", selectedOption.id);
        openPlanOnce();

        if (selectedOption.sendsLead) {
            if (leadSource !== null) {
                void sendLead(
                    {
                        source: leadSource,
                        ctaText: selectedOption.ctaText,
                        formLocation: buttonCategory,
                        confirmationSubject: selectedOption.confirmationSubject
                    },
                    searchStages[selectedOption.id]
                );
            }

            return;
        }

        if (showPriceNegotiation("content_only")) {
            return;
        }

        onContentOnly();
    };

    const handleSelectOption = (optionId: UnlockChoiceOptionId) => {
        if (!isSending) {
            trackUnlockChoiceButtonClick(trackingContext, "select", optionId);
            setSelectedOptionId(optionId);
        }
    };

    const handleClose = () => {
        if (!data) {
            onClose();
            return;
        }

        if (sentLead) {
            reportSentLead();
            return;
        }

        if (isSending) {
            return;
        }

        if (priceNegotiationView) {
            trackUnlockChoiceExitButtonClick(trackingContext, "rp_price_negotiate_close");
            priceNegotiation.markDismissed();
            continueAfterPriceNegotiation(priceNegotiationView.exit);
            return;
        }

        trackUnlockChoiceButtonClick(trackingContext, "close", selectedOption.id);

        if (showPriceNegotiation("close")) {
            return;
        }

        requestClose();
    };

    return {
        isOpen,
        firstName: getUserFirstName(userProfile?.name),
        selectedOptionId: selectedOption.id,
        isSending,
        isConfirmDisabled,
        confirmation: sentLead ? {vendorName: data?.vendorName ?? null, subject: sentLead.subject} : null,
        isPriceNegotiationShown: isPriceNegotiation,
        isPriceNegotiationArmed,
        handleSelectOption,
        handleConfirm,
        handleClose,
        handleAfterClose
    };
};
